"use strict";
var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DialogTrigger = exports.DialogCloseButton = exports.DialogHeading = exports.Dialog = void 0;
var styled_1 = __importDefault(require("@emotion/styled"));
var react_1 = __importDefault(require("react"));
var react_aria_components_1 = require("react-aria-components");
var theme_1 = require("../theme");
var Buttons_1 = require("./Buttons");
/**
 * A dialog is a window overlaid on either the primary window or another dialog window.
 * Dialogs are used to make the user focus on specific content or actions.
 *
 * @see {@link https://react-spectrum.adobe.com/react-aria/Dialog.html}
 * @see {@link https://compass.circleci.com/?path=/story/rac-dialog--default}
 *
 * @example
 * ```tsx
 * <DialogTrigger>
 *   <PrimaryButton>Trigger dialog</PrimaryButton>
 *   <ModalOverlay>
 *     <Modal>
 *       <Dialog>
 *         <DialogHeading>Some dialog</DialogHeading>
 *             <Text>Dialog body</Text>
 *             <Flex
 *               flexDirection="row"
 *               justifyContent="flex-end"
 *               gap="space16"
 *             >
 *               <SecondaryButton slot="close">Cancel</SecondaryButton>
 *               <PrimaryButton>Some action</PrimaryButton>
 *             </Flex>
 *       </Dialog>
 *     </Modal>
 *   </ModalOverlay>
 * </DialogTrigger>
 * ```
 *
 * @param children - The content to display in the dialog. Can be a function that receives a close handler
 * @param className - The CSS className for the element
 * @param style - The inline style for the element
 * @param role - The accessibility role for the dialog. Defaults to 'dialog'
 * @param id - The element's unique identifier
 * @param aria-label - Defines a string value that labels the current element
 * @param aria-labelledby - Identifies the element (or elements) that labels the current element
 * @param aria-describedby - Identifies the element (or elements) that describes the object
 * @param aria-details - Identifies the element (or elements) that provide a detailed, extended description for the object
 */
exports.Dialog = (0, styled_1.default)(react_aria_components_1.Dialog)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  border-radius: ", ";\n  background: ", ";\n  color: ", ";\n  border: ", " solid ", ";\n  padding: ", " ", ";\n  display: flex;\n  flex-direction: column;\n  gap: ", ";\n  min-width: 480px;\n  outline: none;\n  position: relative;\n"], ["\n  border-radius: ", ";\n  background: ", ";\n  color: ", ";\n  border: ", " solid ", ";\n  padding: ", " ", ";\n  display: flex;\n  flex-direction: column;\n  gap: ", ";\n  min-width: 480px;\n  outline: none;\n  position: relative;\n"])), theme_1.theme.radii.card, theme_1.theme.colors.surface300, theme_1.theme.colors.contentPrimary, theme_1.theme.borderWidths.regular, theme_1.theme.colors.borderNeutral, theme_1.theme.space.space24, theme_1.theme.space.space24, theme_1.theme.space.space24);
/**
 * A heading for the dialog.
 *
 * @example
 * ```tsx
 * <DialogHeading>Dialog Title</DialogHeading>
 * ```
 *
 * @param children - The content to display in the heading
 */
exports.DialogHeading = (0, styled_1.default)(react_aria_components_1.Heading)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  font-size: ", ";\n  font-weight: ", ";\n  line-height: ", ";\n  margin: 0;\n  max-width: calc(100% - ", ");\n"], ["\n  font-size: ", ";\n  font-weight: ", ";\n  line-height: ", ";\n  margin: 0;\n  max-width: calc(100% - ", ");\n"])), theme_1.theme.fontSizes.fontsize20, theme_1.theme.fontWeights.medium, theme_1.theme.lineHeights.lineheight28, theme_1.theme.space.space32);
/**
 * A button that closes the dialog when pressed.
 *
 * @example
 * ```tsx
 * <DialogCloseButton slot="close">
 *   <CancelIcon />
 * </DialogCloseButton>
 * ```
 *
 * @param slot - The slot to place the button in. Must be "close"
 * @param children - The icon to display inside the button
 * @param onPress - Handler that is called when the button is pressed
 */
exports.DialogCloseButton = (0, styled_1.default)(Buttons_1.IconButton)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  position: absolute;\n  top: ", ";\n  right: ", ";\n"], ["\n  position: absolute;\n  top: ", ";\n  right: ", ";\n"])), theme_1.theme.space.space16, theme_1.theme.space.space16);
/**
 * A trigger component that opens a dialog when pressed.
 *
 * @example
 * ```tsx
 * <DialogTrigger>
 *   <PrimaryButton>Open Dialog</PrimaryButton>
 *   <ModalOverlay>
 *     <Modal>
 *       <Dialog>
 *         // Dialog content
 *       </Dialog>
 *     </Modal>
 *   </ModalOverlay>
 * </DialogTrigger>
 * ```
 *
 * @param children - The trigger element and dialog content to display
 * @param isOpen - Whether the dialog is open by default (controlled).
 * @param defaultOpen - Whether the dialog is open by default (uncontrolled).
 * @param onOpenChange - Handler that is called when the dialog opens or closes
 */
var DialogTrigger = function (_a) {
    var children = _a.children, rest = __rest(_a, ["children"]);
    return (react_1.default.createElement(react_aria_components_1.DialogTrigger, __assign({}, rest), children));
};
exports.DialogTrigger = DialogTrigger;
var templateObject_1, templateObject_2, templateObject_3;
//# sourceMappingURL=Dialog.js.map